<script>
import {
  iFeaturedIcon,
  iFeaturedIconSizes,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import RiskBadge from '@/V2/components/status/RiskBadge.vue';
import { RISK_LEVEL } from '@/constants/riskLevels';
import DocumentReviewStatusBadge from '@/V2/components/status/DocumentReviewStatusBadge.vue';
import { DOCUMENT_REVIEW_STATUS } from '@/constants/documentStatus';
import { getWebBaseUrl } from '@/environment';

export default {
  name: 'DocumentSearchResult',
  components: {
    iFeaturedIcon,
    RiskBadge,
    DocumentReviewStatusBadge,
    iButton,
    iKeyIcon,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      iFeaturedIconSizes,
      iButtonSizes,
      iButtonStyles,
      DOCUMENT_REVIEW_STATUS,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
    };
  },
  computed: {
    riskLevel() {
      if (this.document?.risk_level) {
        return RISK_LEVEL[this.document.risk_level];
      }

      const agentRiskLevel =
        this.document?.document_review_agent_v1?.output?.risk_level;
      if (agentRiskLevel) {
        return RISK_LEVEL[agentRiskLevel];
      }

      const trustScore = this.document?.trust_score;

      if (typeof trustScore !== 'number') {
        return null;
      }

      if (trustScore <= 24) {
        return RISK_LEVEL.HIGH;
      } else if (trustScore <= 74) {
        return RISK_LEVEL.MEDIUM;
      } else {
        return RISK_LEVEL.LOW;
      }
    },
    formattedDate() {
      if (!this.document.created_at) return '';
      const date = new Date(this.document.created_at);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    isDocumentNameSearchMatch() {
      return this.document.name
        .toLowerCase()
        .includes(this.searchQuery?.toLowerCase());
    },
    isDocumentContentSearchMatch() {
      return this.isDocumentNameSearchMatch === false;
    },
    shouldShowDocumentReviewStatusBadge() {
      return (
        this.document?.review_status &&
        this.document?.review_status !== DOCUMENT_REVIEW_STATUS.REQUIRES_REVIEW
      );
    },
  },
  methods: {
    onResultClicked() {
      this.$emit('click');
    },
    onOpenInNewTabClicked() {
      window.open(
        `${getWebBaseUrl()}/#/customer/${this.document.customer_id}/document/${this.document.id}`,
        '_blank',
      );
    },
  },
};
</script>

<template>
  <div class="result-item d-flex align-items-center i-gap-4">
    <router-link
      :to="{
        name: 'document-view',
        params: {
          customer_id: document.customer_id,
          document_id: document.id,
        },
      }"
      class="result-link"
      data-dd-action-name="global-search-result-document"
      @click="onResultClicked"
    >
      <i-key-icon
        class="avatar"
        icon="medium-file"
        :shape="iKeyIconShape.SQUARE"
        :type="iKeyIconType.NEUTRAL"
        :icon-style="iKeyIconStyle.OUTLINE"
      />

      <div class="content-wrapper">
        <div class="content">
          <div class="name text-truncate">{{ document.name }}</div>
          <div class="meta text-truncate">
            {{ document.customer_name }} • {{ formattedDate }}
            <template v-if="isDocumentContentSearchMatch">
              • In document:
              <span class="highlight-text">{{ searchQuery }}</span>
            </template>
          </div>
        </div>

        <div class="badges d-flex align-items-center i-gap-2">
          <risk-badge
            v-if="riskLevel"
            :risk-level="riskLevel"
            class="flex-shrink-0"
          ></risk-badge>

          <document-review-status-badge
            v-if="shouldShowDocumentReviewStatusBadge"
            :review-status="document.review_status"
            class="flex-shrink-0"
          ></document-review-status-badge>
        </div>
      </div>

      <i-button
        type="neutral"
        :button-style="iButtonStyles.SOLID_LIGHT"
        left-icon="medium-link-square-1"
        :size="iButtonSizes.SMALL"
        class="new-tab-open-button ml-2"
        @button-click="onOpenInNewTabClicked"
      />
    </router-link>
  </div>
</template>

<style lang="scss" scoped>
.result-item {
  width: 100%;
}

.result-link {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
  text-decoration: none;
  color: inherit;
  width: 100%;
  min-width: 0;

  .badges {
    display: flex;
  }

  .new-tab-open-button {
    display: none;
  }

  &:hover {
    background-color: $light-neutral-20;
    cursor: pointer;

    .new-tab-open-button {
      display: flex;
    }

    .badges {
      display: none;
    }
  }
}

.content-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  .name {
    font-weight: 500;
    color: $light-neutral-120;
    min-width: 0;
  }

  .meta {
    color: $light-neutral-100;
    font-size: 0.875rem;
    min-width: 0;
  }
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
